if (!customElements.get('calc-shipping-rate-block')) {
  const STORAGE_KEY = 'f-cart-selected-shipping-rate';
  const ADDRESS_STORAGE_KEY = 'f-cart-selected-shipping-address';
  const PENDING_CART_ZIP_KEY = 'ecomhard-pending-cart-zip';
  const ZIP_LENGTH = 8;
  const DEBOUNCE_TIME = 550;

  const BRAZIL_PROVINCES_BY_ZIP = [
    { min: 1000, max: 19999, province: 'São Paulo' },
    { min: 20000, max: 28999, province: 'Rio de Janeiro' },
    { min: 29000, max: 29999, province: 'Espírito Santo' },
    { min: 30000, max: 39999, province: 'Minas Gerais' },
    { min: 40000, max: 48999, province: 'Bahia' },
    { min: 49000, max: 49999, province: 'Sergipe' },
    { min: 50000, max: 56999, province: 'Pernambuco' },
    { min: 57000, max: 57999, province: 'Alagoas' },
    { min: 58000, max: 58999, province: 'Paraíba' },
    { min: 59000, max: 59999, province: 'Rio Grande do Norte' },
    { min: 60000, max: 63999, province: 'Ceará' },
    { min: 64000, max: 64999, province: 'Piauí' },
    { min: 65000, max: 65999, province: 'Maranhão' },
    { min: 66000, max: 68899, province: 'Pará' },
    { min: 68900, max: 68999, province: 'Amapá' },
    { min: 69000, max: 69299, province: 'Amazonas' },
    { min: 69300, max: 69399, province: 'Roraima' },
    { min: 69400, max: 69899, province: 'Amazonas' },
    { min: 69900, max: 69999, province: 'Acre' },
    { min: 70000, max: 72799, province: 'Distrito Federal' },
    { min: 72800, max: 72999, province: 'Goiás' },
    { min: 73000, max: 73699, province: 'Distrito Federal' },
    { min: 73700, max: 76799, province: 'Goiás' },
    { min: 77000, max: 77999, province: 'Tocantins' },
    { min: 78000, max: 78899, province: 'Mato Grosso' },
    { min: 78900, max: 78999, province: 'Rondônia' },
    { min: 79000, max: 79999, province: 'Mato Grosso do Sul' },
    { min: 80000, max: 87999, province: 'Paraná' },
    { min: 88000, max: 89999, province: 'Santa Catarina' },
    { min: 90000, max: 99999, province: 'Rio Grande do Sul' },
  ];

  const UF_TO_STATE_NAME = {
    AC: 'Acre',
    AL: 'Alagoas',
    AP: 'Amapá',
    AM: 'Amazonas',
    BA: 'Bahia',
    CE: 'Ceará',
    DF: 'Distrito Federal',
    ES: 'Espírito Santo',
    GO: 'Goiás',
    MA: 'Maranhão',
    MT: 'Mato Grosso',
    MS: 'Mato Grosso do Sul',
    MG: 'Minas Gerais',
    PA: 'Pará',
    PB: 'Paraíba',
    PR: 'Paraná',
    PE: 'Pernambuco',
    PI: 'Piauí',
    RJ: 'Rio de Janeiro',
    RN: 'Rio Grande do Norte',
    RS: 'Rio Grande do Sul',
    RO: 'Rondônia',
    RR: 'Roraima',
    SC: 'Santa Catarina',
    SP: 'São Paulo',
    SE: 'Sergipe',
    TO: 'Tocantins',
  };

  const FORTALEZA_FREE_SHIPPING_PREFIXES = [
    '600', '601', '602', '603', '604', '605', '606', '607', '608',
    '609', '610', '611', '612', '613', '614', '615', '616', '617',
    '618', '619',
  ];

  const moneyFormat = () => window.FoxThemeSettings?.money_format || '${{amount}}';

  function toDigits(value) {
    return String(value || '').replace(/\D/g, '').slice(0, ZIP_LENGTH);
  }

  function formatZip(value) {
    const digits = toDigits(value);

    if (digits.length > 5) {
      return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    }

    return digits;
  }

  function normalizeText(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  function normalizeCountry(country) {
    const value = String(country || '').trim();

    if (!value) return 'Brazil';

    const normalized = normalizeText(value);

    if (normalized === 'brasil' || normalized === 'br') return 'Brazil';

    return value;
  }

  function detectBrazilProvinceFromZip(zip) {
    const digits = toDigits(zip);

    if (digits.length < ZIP_LENGTH) return '';

    const prefix = Number.parseInt(digits.slice(0, 5), 10);

    if (!Number.isFinite(prefix)) return '';

    const match = BRAZIL_PROVINCES_BY_ZIP.find((range) => {
      return prefix >= range.min && prefix <= range.max;
    });

    return match ? match.province : '';
  }

  function provinceCodeToName(provinceCode) {
    const code = String(provinceCode || '').trim().toUpperCase();

    return UF_TO_STATE_NAME[code] || code;
  }

  function priceToCents(rate) {
    if (!rate) return 0;

    if (typeof rate.price_cents === 'number') {
      return rate.price_cents;
    }

    const rawPrice = String(rate.price || '0')
      .replace(/[^\d,.-]/g, '')
      .replace(',', '.');

    const parsedPrice = Number.parseFloat(rawPrice);

    return Number.isFinite(parsedPrice) ? Math.round(parsedPrice * 100) : 0;
  }

  function formatPrice(cents) {
    if (typeof formatMoney === 'function') {
      return formatMoney(cents, moneyFormat());
    }

    return (Number(cents || 0) / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  }

  function parseMoneyToCents(value) {
    const text = String(value || '').trim();

    if (!text) return 0;

    let normalized = text.replace(/[^\d,.-]/g, '');

    if (normalized.includes(',')) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    }

    const parsed = Number.parseFloat(normalized);

    return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
  }

  function getDeliveryText(rate) {
    if (!rate) return '';

    if (Array.isArray(rate.delivery_days) && rate.delivery_days.length > 0) {
      const days = rate.delivery_days
        .map((day) => Number.parseInt(day, 10))
        .filter((day) => Number.isFinite(day));

      if (days.length > 0) {
        const maxDays = Math.max(...days);
        return `${maxDays} DIAS ÚTEIS`;
      }
    }

    const deliveryText = rate.delivery_date || rate.delivery_range || rate.description || '';

    return String(deliveryText).trim().toUpperCase();
  }

  function isFortalezaZip(zip) {
    const digits = toDigits(zip);

    if (!digits) return false;

    return FORTALEZA_FREE_SHIPPING_PREFIXES.some((prefix) => digits.startsWith(prefix));
  }

  function isLocalDeliveryRate(rate) {
    const name = normalizeText(rate?.name || '');

    return (
      name === 'local delivery' ||
      name.includes('local delivery') ||
      name === 'entrega local' ||
      name.includes('entrega local')
    );
  }

  function isFortalezaFreeShippingRate(rate, zip) {
    const name = normalizeText(rate?.name || '');

    if (name.includes('frete gratis fortaleza')) return true;

    return isFortalezaZip(zip) && isLocalDeliveryRate(rate) && priceToCents(rate) === 0;
  }

  function getShippingRateDisplayName(rate) {
    return String(rate?.name || '').trim();
  }

  function buildRateLabel(rate) {
    const rateName = getShippingRateDisplayName(rate);
    const price = formatPrice(priceToCents(rate));
    const deliveryText = getDeliveryText(rate);

    return `${rateName} - ${price}${deliveryText ? ` (${deliveryText})` : ''}`;
  }

  function escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function getStoredShipping() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    } catch (error) {
      return null;
    }
  }

  function setStoredShipping(payload) {
    try {
      if (!payload) {
        sessionStorage.removeItem(STORAGE_KEY);
        return;
      }

      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
      // sessionStorage pode estar bloqueado em alguns navegadores.
    }
  }

  function getStoredAddress() {
    try {
      return JSON.parse(sessionStorage.getItem(ADDRESS_STORAGE_KEY) || 'null');
    } catch (error) {
      return null;
    }
  }

  function setStoredAddress(address) {
    try {
      if (!address) {
        sessionStorage.removeItem(ADDRESS_STORAGE_KEY);
        return;
      }

      sessionStorage.setItem(ADDRESS_STORAGE_KEY, JSON.stringify(address));
    } catch (error) {
      // sessionStorage pode estar bloqueado em alguns navegadores.
    }
  }

  function getErrorMessageFromResult(result) {
    if (!result || typeof result !== 'object') return '';

    const firstError = Object.values(result).find((value) => {
      return Array.isArray(value) && value.length;
    });

    return firstError ? firstError[0] : '';
  }

  async function fetchAddressByZip(zip) {
    const digits = toDigits(zip);

    if (digits.length !== ZIP_LENGTH) return null;

    try {
      const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);

      if (!response.ok) return null;

      const data = await response.json();

      if (!data || data.erro) return null;

      const provinceCode = String(data.uf || '').trim().toUpperCase();
      const province = provinceCodeToName(provinceCode);

      return {
        zip: formatZip(digits),
        zipDigits: digits,
        address1: String(data.logradouro || '').trim(),
        neighborhood: String(data.bairro || '').trim(),
        city: String(data.localidade || '').trim(),
        provinceCode,
        province,
        country: 'Brazil',
      };
    } catch (error) {
      console.warn('[ECOMHARD] Não foi possível buscar endereço pelo CEP:', error);
      return null;
    }
  }

  function buildFallbackAddress(zip) {
    const digits = toDigits(zip);
    const province = detectBrazilProvinceFromZip(digits);

    return {
      zip: formatZip(digits),
      zipDigits: digits,
      address1: '',
      neighborhood: '',
      city: '',
      provinceCode: '',
      province,
      country: 'Brazil',
    };
  }

  function addCheckoutAddressParams(url, address) {
    if (!url || !address) return url;

    const zip = address.zip || address.zipDigits || '';
    const country = address.country || 'Brazil';
    const province = address.province || '';
    const provinceCode = address.provinceCode || '';
    const city = address.city || '';
    const address1 = address.address1 || '';

    url.searchParams.set('checkout[shipping_address][zip]', zip);
    url.searchParams.set('checkout[shipping_address][country]', country);
    url.searchParams.set('checkout[shipping_address][province]', province);
    url.searchParams.set('checkout[shipping_address][city]', city);
    url.searchParams.set('checkout[shipping_address][address1]', address1);

    if (provinceCode) {
      url.searchParams.set('checkout[shipping_address][province_code]', provinceCode);
    }

    return url;
  }

  async function fetchCartState() {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) return null;

    return response.json();
  }

  function appendCartAttributesToPermalink(url, cart) {
    if (!url || !cart) return;

    if (cart.note) {
      url.searchParams.set('note', cart.note);
    }

    if (cart.attributes && typeof cart.attributes === 'object') {
      Object.entries(cart.attributes).forEach(([key, value]) => {
        if (value === null || typeof value === 'undefined') return;

        url.searchParams.set(`attributes[${key}]`, String(value));
      });
    }

    const discountCodes = [];

    if (Array.isArray(cart.cart_level_discount_applications)) {
      cart.cart_level_discount_applications.forEach((discount) => {
        if (discount?.type === 'discount_code' && discount?.title) {
          discountCodes.push(discount.title);
        }
      });
    }

    if (Array.isArray(cart.items)) {
      cart.items.forEach((item) => {
        if (!Array.isArray(item.line_level_discount_allocations)) return;

        item.line_level_discount_allocations.forEach((allocation) => {
          const application = allocation?.discount_application;

          if (application?.type === 'discount_code' && application?.title) {
            discountCodes.push(application.title);
          }
        });
      });
    }

    const uniqueDiscountCodes = [...new Set(discountCodes)];

    if (uniqueDiscountCodes.length) {
      url.searchParams.set('discount', uniqueDiscountCodes.join(','));
    }
  }

  async function buildCartPermalinkCheckoutUrl(address) {
    const cart = await fetchCartState();

    if (!cart || !Array.isArray(cart.items) || !cart.items.length) {
      const fallbackCheckoutUrl = new URL('/checkout', window.location.origin);
      addCheckoutAddressParams(fallbackCheckoutUrl, address);
      return fallbackCheckoutUrl.toString();
    }

    const cartLines = cart.items
      .filter((item) => Number(item.quantity || 0) > 0)
      .map((item) => {
        const variantId = item.variant_id || item.id;
        const quantity = Number(item.quantity || 0);

        if (!variantId || !quantity) return '';

        return `${variantId}:${quantity}`;
      })
      .filter(Boolean);

    if (!cartLines.length) {
      const fallbackCheckoutUrl = new URL('/checkout', window.location.origin);
      addCheckoutAddressParams(fallbackCheckoutUrl, address);
      return fallbackCheckoutUrl.toString();
    }

    const cartPermalinkUrl = new URL(`/cart/${cartLines.join(',')}`, window.location.origin);

    addCheckoutAddressParams(cartPermalinkUrl, address);
    appendCartAttributesToPermalink(cartPermalinkUrl, cart);

    return cartPermalinkUrl.toString();
  }

  async function fetchShippingRates({ zip, country, province, signal }) {
    const url = new URL('/cart/shipping_rates.json', window.location.origin);

    url.searchParams.set('shipping_address[zip]', toDigits(zip));
    url.searchParams.set('shipping_address[country]', country);
    url.searchParams.set('shipping_address[province]', province);

    const response = await fetch(url.toString(), { signal });
    const result = await response.json();

    return result;
  }

  function refreshCartDrawerAfterBackNavigation() {
    if (window.__ecomhardCartDrawerBackRefreshBound) return;

    window.__ecomhardCartDrawerBackRefreshBound = true;

    window.addEventListener('pageshow', (event) => {
      const navigationEntry = performance.getEntriesByType
        ? performance.getEntriesByType('navigation')[0]
        : null;

      const isBackForward = event.persisted || navigationEntry?.type === 'back_forward';

      if (!isBackForward) return;

      window.setTimeout(() => {
        document.dispatchEvent(
          new CustomEvent('cart:refresh', {
            detail: {
              open: false,
            },
          })
        );

        if (window.FoxThemeCartHelpers?.getState) {
          window.FoxThemeCartHelpers.getState().then((state) => {
            if (window.FoxThemeCartHelpers?.updateCartCount) {
              window.FoxThemeCartHelpers.updateCartCount(state.item_count);
            }
          });
        }
      }, 80);
    });
  }


  function getPendingCartZip() {
    try {
      return sessionStorage.getItem(PENDING_CART_ZIP_KEY) || localStorage.getItem(PENDING_CART_ZIP_KEY) || '';
    } catch (error) {
      return '';
    }
  }

  function setPendingCartZip(zip) {
    const digits = toDigits(zip);

    try {
      if (!digits || digits.length !== ZIP_LENGTH) {
        sessionStorage.removeItem(PENDING_CART_ZIP_KEY);
        localStorage.removeItem(PENDING_CART_ZIP_KEY);
        return;
      }

      sessionStorage.setItem(PENDING_CART_ZIP_KEY, digits);
      localStorage.setItem(PENDING_CART_ZIP_KEY, digits);
    } catch (error) {
      // Storage pode estar bloqueado.
    }
  }

  function clearPendingCartZip() {
    try {
      sessionStorage.removeItem(PENDING_CART_ZIP_KEY);
      localStorage.removeItem(PENDING_CART_ZIP_KEY);
    } catch (error) {
      // Storage pode estar bloqueado.
    }
  }

  function broadcastPendingCartZip(zip) {
    setPendingCartZip(zip);

    try {
      document.dispatchEvent(
        new CustomEvent('ecomhard:shipping:pending-zip', {
          detail: {
            zip: toDigits(zip),
          },
        })
      );
    } catch (error) {
      // Evita quebrar navegadores antigos.
    }
  }

  function findCartLineByVariant(cart, variantId) {
    if (!cart || !Array.isArray(cart.items)) return null;

    return cart.items.find((item) => {
      return Number(item.variant_id || item.id) === Number(variantId);
    }) || null;
  }

  async function addVariantToCart(variantId, quantity) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity: Number(quantity),
          },
        ],
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result?.description || result?.message || 'Não foi possível adicionar o produto ao carrinho.');
    }

    return result;
  }

  async function changeCartLineQuantity(lineIdentifier, quantity) {
    if (!lineIdentifier && lineIdentifier !== 0) return null;

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: lineIdentifier,
        quantity: Number(quantity),
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result?.description || result?.message || 'Não foi possível restaurar o carrinho.');
    }

    return result;
  }

  async function refreshCartUi() {
    try {
      if (window.FoxThemeCartHelpers?.getState) {
        const state = await window.FoxThemeCartHelpers.getState();

        if (window.FoxThemeCartHelpers?.updateCartCount) {
          window.FoxThemeCartHelpers.updateCartCount(state.item_count);
        }
      }

      document.dispatchEvent(
        new CustomEvent('cart:refresh', {
          detail: {
            open: false,
          },
        })
      );
    } catch (error) {
      // Evita quebrar o cálculo caso o tema não tenha helpers de carrinho.
    }
  }


  customElements.define(
    'calc-shipping-rate-block',
    class FooterCalcShippingRates extends HTMLElement {
      constructor() {
        super();

        this.rates = [];
        this.selectedRate = null;
        this.selectedRateIndex = 0;
        this.debounceTimer = null;
        this.abortController = null;
        this.initialized = false;
        this.checkoutAddress = null;
        this.isRedirectingToCheckout = false;
        this.context = 'cart';

        this.handleInput = this.handleInput.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleRateChange = this.handleRateChange.bind(this);
        this.handleCartUpdate = this.handleCartUpdate.bind(this);
        this.handleClear = this.handleClear.bind(this);
        this.handleCalculateClick = this.handleCalculateClick.bind(this);
        this.handleCheckoutClick = this.handleCheckoutClick.bind(this);
        this.handleCheckoutSubmit = this.handleCheckoutSubmit.bind(this);
        this.handlePendingZip = this.handlePendingZip.bind(this);
      }

      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.context = this.dataset.context || 'cart';

        this.selectors = {
          zipCode: this.context === 'product' ? '[data-shipping-zip]' : '[name="address[zip]"]',
          province: '[name="address[province]"]',
          country: '[name="address[country]"]',
          message: '[data-shipping-message]',
          price: '[data-shipping-price]',
          clearButton: '[data-shipping-clear]',
          calculateButton: '[data-shipping-calculate]',
          results: '[data-shipping-results]',
          total: '[data-shipping-total]',
          totalPrice: '[data-shipping-total-price]',
          variantsJson: '[data-product-variants-json]',
        };

        this.domNodes = {
          zipCode: this.querySelector(this.selectors.zipCode),
          province: this.querySelector(this.selectors.province),
          country: this.querySelector(this.selectors.country),
          message: this.querySelector(this.selectors.message),
          price: this.querySelector(this.selectors.price),
          clearButton: this.querySelector(this.selectors.clearButton),
          calculateButton: this.querySelector(this.selectors.calculateButton),
          results: this.querySelector(this.selectors.results),
          total: this.querySelector(this.selectors.total),
          totalPrice: this.querySelector(this.selectors.totalPrice),
          variantsJson: this.querySelector(this.selectors.variantsJson),
        };

        if (!this.domNodes.zipCode || !this.domNodes.message) return;

        if (this.context === 'cart' && !this.domNodes.price) return;

        if (
          this.context === 'product' &&
          (!this.domNodes.calculateButton || !this.domNodes.results || !this.domNodes.total || !this.domNodes.totalPrice)
        ) {
          return;
        }

        this.domNodes.zipCode.value = formatZip(this.domNodes.zipCode.value);

        this.domNodes.zipCode.addEventListener('input', this.handleInput);
        this.domNodes.zipCode.addEventListener('keydown', this.handleKeydown);
        this.domNodes.zipCode.addEventListener('blur', () => {
          this.domNodes.zipCode.value = formatZip(this.domNodes.zipCode.value);
        });

        if (this.domNodes.clearButton) {
          this.domNodes.clearButton.addEventListener('click', this.handleClear);
        }

        if (this.domNodes.calculateButton) {
          this.defaultButtonLabel = this.domNodes.calculateButton.textContent.trim();
          this.loadingLabel = this.dataset.loadingLabel || 'CALCULANDO...';
          this.domNodes.calculateButton.addEventListener('click', this.handleCalculateClick);
        }

        if (this.context === 'product') {
          this.variants = this.getVariants();
        }

        document.addEventListener('cart:updated', this.handleCartUpdate);

        if (this.context === 'cart') {
          document.addEventListener('click', this.handleCheckoutClick, true);
          document.addEventListener('submit', this.handleCheckoutSubmit, true);
          document.addEventListener('ecomhard:shipping:pending-zip', this.handlePendingZip);
        }

        const pubSubEvents = window.PUB_SUB_EVENTS || (typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null);

        if (window.FoxThemeEvents && pubSubEvents?.cartUpdate) {
          this.cartUpdateUnsubscriber = window.FoxThemeEvents.subscribe(
            pubSubEvents.cartUpdate,
            this.handleCartUpdate
          );
        }

        if (this.context === 'cart') {
          refreshCartDrawerAfterBackNavigation();
          this.updateTotal(null);
          this.restoreZipFromPdpOrPreviousShipping();
        }
      }

      disconnectedCallback() {
        document.removeEventListener('cart:updated', this.handleCartUpdate);

        if (this.context === 'cart') {
          document.removeEventListener('click', this.handleCheckoutClick, true);
          document.removeEventListener('submit', this.handleCheckoutSubmit, true);
          document.removeEventListener('ecomhard:shipping:pending-zip', this.handlePendingZip);
        }

        if (typeof this.cartUpdateUnsubscriber === 'function') {
          this.cartUpdateUnsubscriber();
        }

        if (this.domNodes?.clearButton) {
          this.domNodes.clearButton.removeEventListener('click', this.handleClear);
        }

        if (this.domNodes?.calculateButton) {
          this.domNodes.calculateButton.removeEventListener('click', this.handleCalculateClick);
        }

        if (this.abortController) {
          this.abortController.abort();
        }
      }

      getVariants() {
        try {
          return JSON.parse(this.domNodes.variantsJson?.textContent || '[]');
        } catch (error) {
          return [];
        }
      }

      isCheckoutEventFromThisCart(checkoutButton) {
        if (!checkoutButton || this.context !== 'cart') return false;

        const zip = toDigits(this.domNodes?.zipCode?.value || '');

        if (zip.length !== ZIP_LENGTH) return false;

        const currentCartDrawer = this.closest('cart-drawer');
        const buttonCartDrawer = checkoutButton.closest('cart-drawer');

        if (currentCartDrawer && buttonCartDrawer) {
          return currentCartDrawer === buttonCartDrawer;
        }

        return true;
      }

      handleCartUpdate() {
        window.setTimeout(() => {
          this.updateTotal();
        }, 100);
      }

      handlePendingZip(event) {
        if (this.context !== 'cart') return;

        const zip = toDigits(event?.detail?.zip || getPendingCartZip());

        if (zip.length !== ZIP_LENGTH) return;

        this.domNodes.zipCode.value = formatZip(zip);
        this.fetchRates({ keepSelectedIndex: false });
      }

      handleInput(event) {
        const formattedZip = formatZip(event.target.value);
        event.target.value = formattedZip;

        const zipDigits = toDigits(formattedZip);

        this.checkoutAddress = null;

        if (this.context === 'cart') {
          setStoredAddress(null);
        }

        if (this.context === 'product') {
          clearPendingCartZip();
          this.clearProductResults();
        }

        if (zipDigits.length < ZIP_LENGTH) {
          this.clearRates();
          return;
        }

        if (this.context === 'cart') {
          window.clearTimeout(this.debounceTimer);

          this.debounceTimer = window.setTimeout(() => {
            this.fetchRates();
          }, DEBOUNCE_TIME);
        }
      }

      handleKeydown(event) {
        if (event.key !== 'Enter') return;

        event.preventDefault();

        const zipDigits = toDigits(this.domNodes.zipCode.value);

        if (zipDigits.length === ZIP_LENGTH) {
          this.fetchRates();
        }
      }

      handleCalculateClick(event) {
        event.preventDefault();
        this.fetchRates();
      }

      handleRateChange(event) {
        const rawIndex = event.target.value || event.target.dataset.ehRateIndex || '0';
        const index = Number.parseInt(rawIndex, 10);

        this.selectRate(Number.isFinite(index) ? index : 0);
      }

      handleClear(event) {
        event.preventDefault();

        if (this.abortController) {
          this.abortController.abort();
        }

        window.clearTimeout(this.debounceTimer);

        this.domNodes.zipCode.value = '';
        this.domNodes.message.innerHTML = '';
        this.rates = [];
        this.checkoutAddress = null;

        if (this.context === 'cart') {
          setStoredAddress(null);
        }

        if (this.context === 'product') {
          clearPendingCartZip();
        }

        this.clearSelectedShipping(true);
        this.setLoading(false);

        this.domNodes.zipCode.focus();
      }

      handleCheckoutClick(event) {
        if (this.context !== 'cart') return;
        if (!(event.target instanceof Element)) return;

        const checkoutButton = event.target.closest(
          'button[name="checkout"], input[name="checkout"], [data-checkout-button], a[href="/checkout"], a[href*="/checkout"]'
        );

        if (!checkoutButton) return;
        if (!this.isCheckoutEventFromThisCart(checkoutButton)) return;

        event.preventDefault();
        event.stopPropagation();

        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }

        this.redirectToCheckout();
      }

      handleCheckoutSubmit(event) {
        if (this.context !== 'cart') return;

        const form = event.target;

        if (!(form instanceof HTMLFormElement)) return;

        const checkoutButton = form.querySelector(
          'button[name="checkout"], input[name="checkout"], [data-checkout-button]'
        );

        if (!checkoutButton) return;
        if (!this.isCheckoutEventFromThisCart(checkoutButton)) return;

        event.preventDefault();
        event.stopPropagation();

        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }

        this.redirectToCheckout();
      }

      async getCheckoutAddressFromCurrentZip() {
        const zip = toDigits(this.domNodes.zipCode?.value || '');

        if (zip.length !== ZIP_LENGTH) {
          return null;
        }

        const freshAddress = await fetchAddressByZip(zip);

        if (freshAddress) {
          this.checkoutAddress = freshAddress;
          setStoredAddress(freshAddress);
          return freshAddress;
        }

        const storedAddress = getStoredAddress();

        if (storedAddress && toDigits(storedAddress.zip || storedAddress.zipDigits) === zip) {
          this.checkoutAddress = storedAddress;
          return storedAddress;
        }

        if (this.checkoutAddress && toDigits(this.checkoutAddress.zip || this.checkoutAddress.zipDigits) === zip) {
          return this.checkoutAddress;
        }

        const fallbackAddress = buildFallbackAddress(zip);

        this.checkoutAddress = fallbackAddress;
        setStoredAddress(fallbackAddress);

        return fallbackAddress;
      }

      async redirectToCheckout() {
        if (this.isRedirectingToCheckout) return;

        this.isRedirectingToCheckout = true;

        try {
          const address = await this.getCheckoutAddressFromCurrentZip();

          if (!address) {
            window.location.assign('/checkout');
            return;
          }

          const checkoutUrl = await buildCartPermalinkCheckoutUrl(address);
          window.__ecomhardLastCheckoutUrl = checkoutUrl;

          window.location.assign(checkoutUrl);
        } catch (error) {
          console.warn('[ECOMHARD] Erro ao redirecionar para checkout:', error);

          try {
            const address = this.checkoutAddress || getStoredAddress();
            const fallbackCheckoutUrl = new URL('/checkout', window.location.origin);

            addCheckoutAddressParams(fallbackCheckoutUrl, address);
            window.__ecomhardLastCheckoutUrl = fallbackCheckoutUrl.toString();

            window.location.assign(fallbackCheckoutUrl.toString());
          } catch (fallbackError) {
            window.location.assign('/checkout');
          }
        }
      }

      async restoreZipFromPdpOrPreviousShipping() {
        const pendingZip = getPendingCartZip();

        if (toDigits(pendingZip).length === ZIP_LENGTH) {
          this.selectedRateIndex = 0;
          this.domNodes.zipCode.value = formatZip(pendingZip);
          await this.fetchRates({ keepSelectedIndex: false });
          return;
        }

        await this.restorePreviousShipping();
      }

      async restorePreviousShipping() {
        if (this.context !== 'cart') return;

        const storedShipping = getStoredShipping();

        if (!storedShipping?.zip) return;

        this.selectedRateIndex = Number(storedShipping.index || 0);
        this.domNodes.zipCode.value = formatZip(storedShipping.zip);

        const storedAddress = getStoredAddress();

        if (storedAddress && toDigits(storedAddress.zip || storedAddress.zipDigits) === toDigits(storedShipping.zip)) {
          this.checkoutAddress = storedAddress;
        }

        await this.fetchRates({ keepSelectedIndex: true });
      }

      async fetchRates(options = {}) {
        if (this.context === 'product') {
          await this.fetchProductRates(options);
          return;
        }

        await this.fetchCartRates(options);
      }

      async fetchCartRates(options = {}) {
        const zip = toDigits(this.domNodes.zipCode.value);

        if (zip.length !== ZIP_LENGTH) return;

        if (this.abortController) {
          this.abortController.abort();
        }

        this.abortController = new AbortController();
        const currentController = this.abortController;

        this.setLoading(true);
        this.domNodes.message.innerHTML = '';

        try {
          const address = await fetchAddressByZip(zip);
          const currentZip = toDigits(this.domNodes.zipCode?.value || '');

          if (currentZip !== zip) return;

          if (address) {
            this.checkoutAddress = address;
            setStoredAddress(address);
          } else {
            this.checkoutAddress = buildFallbackAddress(zip);
            setStoredAddress(this.checkoutAddress);
          }

          const country = normalizeCountry(this.domNodes.country?.value || this.dataset.defaultCountry || 'Brazil');
          let province = '';

          if (country === 'Brazil') {
            province = this.checkoutAddress?.province || detectBrazilProvinceFromZip(zip);
          } else {
            province = String(this.domNodes.province?.value || '').trim();
          }

          if (this.domNodes.province) {
            this.domNodes.province.value = province;
          }

          if (!province) {
            this.renderMessage('Não foi possível identificar o estado desse CEP.');
            this.clearSelectedShipping(false);
            return;
          }

          let result = await fetchShippingRates({
            zip,
            country,
            province,
            signal: this.abortController.signal,
          });

          if (
            (!Array.isArray(result?.shipping_rates) || result.shipping_rates.length === 0) &&
            this.checkoutAddress?.provinceCode
          ) {
            result = await fetchShippingRates({
              zip,
              country,
              province: this.checkoutAddress.provinceCode,
              signal: this.abortController.signal,
            });
          }

          if (Array.isArray(result?.shipping_rates) && result.shipping_rates.length > 0) {
            this.renderRates(result.shipping_rates, options);
            return;
          }

          if (Array.isArray(result?.shipping_rates) && result.shipping_rates.length === 0) {
            this.renderMessage(
              window.FoxThemeStrings?.noShippingRate ||
              'Não encontramos fretes para esse CEP. Você ainda pode finalizar a compra para confirmar no checkout.'
            );
            this.clearSelectedShipping(false);
            return;
          }

          const errorMessage = this.getErrorMessage(result);

          this.renderMessage(errorMessage || window.FoxThemeStrings?.cartError || 'Não foi possível calcular o frete.');
          this.clearSelectedShipping(false);
        } catch (error) {
          if (error.name === 'AbortError') return;

          this.renderMessage(window.FoxThemeStrings?.cartError || 'Não foi possível calcular o frete.');
          this.clearSelectedShipping(false);
        } finally {
          if (this.abortController === currentController) {
            this.setLoading(false);
          }
        }
      }

      async fetchProductRates() {
        const zip = toDigits(this.domNodes.zipCode.value);
        const variantId = this.getCurrentVariantId();
        const quantity = this.getCurrentQuantity();

        this.clearProductResults();

        if (zip.length !== ZIP_LENGTH) {
          this.renderMessage(this.dataset.invalidZipMessage || 'Digite um CEP válido.');
          return;
        }

        if (!variantId) {
          this.renderMessage(this.dataset.errorMessage || 'Não foi possível calcular o frete.');
          return;
        }

        this.setLoading(true);
        this.domNodes.message.innerHTML = '';
        this.domNodes.message.hidden = true;

        let addedToCart = false;
        let temporaryLineKey = null;

        try {
          const address = await fetchAddressByZip(zip);
          const calculationAddress = address || buildFallbackAddress(zip);

          if (!calculationAddress || !calculationAddress.province) {
            throw new Error(this.dataset.errorMessage || 'Não foi possível calcular o frete.');
          }

          const originalCart = await fetchCartState();
          const originalLine = findCartLineByVariant(originalCart, variantId);

          if (!originalLine) {
            const addResult = await addVariantToCart(variantId, quantity);

            addedToCart = true;

            temporaryLineKey =
              addResult?.items?.[0]?.key ||
              addResult?.key ||
              null;

            if (!temporaryLineKey) {
              const cartAfterAdd = await fetchCartState();
              const addedLine = findCartLineByVariant(cartAfterAdd, variantId);

              temporaryLineKey = addedLine?.key || null;
            }
          }

          let result = await fetchShippingRates({
            zip,
            country: 'Brazil',
            province: calculationAddress.province,
          });

          if (
            (!Array.isArray(result?.shipping_rates) || !result.shipping_rates.length) &&
            calculationAddress.provinceCode
          ) {
            result = await fetchShippingRates({
              zip,
              country: 'Brazil',
              province: calculationAddress.provinceCode,
            });
          }

          this.renderRates(result?.shipping_rates || [], {});
        } catch (error) {
          this.clearProductResults();
          clearPendingCartZip();
          this.renderMessage(error?.message || this.dataset.errorMessage || 'Não foi possível calcular o frete.');
        } finally {
          try {
            if (addedToCart) {
              await changeCartLineQuantity(temporaryLineKey || Number(variantId), 0);
              await refreshCartUi();
            }
          } catch (restoreError) {
            console.warn('[ECOMHARD] Não foi possível remover o produto temporário após calcular o frete.', restoreError);
          }

          this.setLoading(false);
        }
      }

      renderRates(rates, options = {}) {
        if (this.context === 'product') {
          this.renderProductRates(rates);
          return;
        }

        this.renderCartRates(rates, options);
      }

      renderCartRates(rates, options = {}) {
        const zip = toDigits(this.domNodes.zipCode.value);

        this.rates = (Array.isArray(rates) ? rates : []).filter((rate) => {
          return !isFortalezaFreeShippingRate(rate, zip);
        });

        this.domNodes.message.innerHTML = '';

        if (!this.rates.length) {
          this.renderMessage(window.FoxThemeStrings?.noShippingRate || 'Não encontramos fretes para esse CEP.');
          this.clearSelectedShipping(false);
          return;
        }

        const select = document.createElement('select');

        select.className = 'f-cart-shipping__select';
        select.setAttribute('aria-label', 'Escolha o frete');

        this.rates.forEach((rate, index) => {
          const option = document.createElement('option');

          option.value = String(index);
          option.textContent = buildRateLabel(rate);
          option.dataset.priceCents = String(priceToCents(rate));

          select.appendChild(option);
        });

        select.addEventListener('change', this.handleRateChange);

        this.domNodes.message.appendChild(select);

        const requestedIndex = options.keepSelectedIndex ? this.selectedRateIndex : 0;
        const safeIndex = this.rates[requestedIndex] ? requestedIndex : 0;

        select.value = String(safeIndex);

        this.selectRate(safeIndex);
      }

      renderProductRates(rates) {
        const zip = toDigits(this.domNodes.zipCode.value);

        this.rates = (Array.isArray(rates) ? rates : []).filter((rate) => {
          return !isFortalezaFreeShippingRate(rate, zip);
        });

        if (!this.rates.length) {
          this.clearProductResults();
          this.renderMessage(this.dataset.noRatesMessage || 'Não encontramos opções de frete para esse CEP.');
          return;
        }

        this.domNodes.message.innerHTML = '';
        this.domNodes.message.hidden = true;

        const radioName = `eh-pdp-shipping-rate-${this.dataset.sectionId || 'section'}-${this.dataset.productId || 'product'}`;

        this.domNodes.results.innerHTML = this.rates.map((rate, index) => {
          const name = String(rate?.name || '').trim();
          const price = formatPrice(priceToCents(rate));
          const delivery = getDeliveryText(rate);

          return `
            <label class="eh-pdp-shipping__result-item">
              <span class="eh-pdp-shipping__result-row">
                <span class="eh-pdp-shipping__result-info">
                  <span class="eh-pdp-shipping__result-name">${escapeHtml(name)}</span>
                  ${delivery ? `<span class="eh-pdp-shipping__result-delivery">${escapeHtml(delivery)}</span>` : ''}
                </span>

                <span class="eh-pdp-shipping__result-side">
                  <span class="eh-pdp-shipping__result-price">${escapeHtml(price)}</span>

                  <span class="eh-pdp-shipping__radio">
                    <input
                      type="radio"
                      class="eh-pdp-shipping__radio-input"
                      name="${escapeHtml(radioName)}"
                      data-eh-rate-index="${index}"
                      value="${index}"
                    >
                    <span class="eh-pdp-shipping__radio-mark"></span>
                  </span>
                </span>
              </span>
            </label>
          `;
        }).join('');

        this.domNodes.results.querySelectorAll('[data-eh-rate-index]').forEach((radio) => {
          radio.addEventListener('change', this.handleRateChange);
        });

        this.domNodes.results.hidden = false;

        this.selectRate(0);
      }

      selectRate(index = 0) {
        const rate = this.rates[index];

        if (!rate) {
          this.clearSelectedShipping(false);
          return;
        }

        const zip = toDigits(this.domNodes.zipCode.value);
        const priceCents = priceToCents(rate);
        const displayName = getShippingRateDisplayName(rate);

        this.selectedRate = rate;
        this.selectedRateIndex = index;

        if (this.context === 'product') {
          const radios = this.domNodes.results.querySelectorAll('[data-eh-rate-index]');

          radios.forEach((radio) => {
            radio.checked = Number.parseInt(radio.dataset.ehRateIndex || '-1', 10) === index;
          });

          this.updateProductTotal(priceCents);
          broadcastPendingCartZip(zip);
          return;
        }

        this.domNodes.price.textContent = formatPrice(priceCents);

        this.classList.add('has-rates');

        if (this.domNodes.clearButton) {
          this.domNodes.clearButton.removeAttribute('hidden');
        }

        this.updateTotal(priceCents);

        setStoredShipping({
          zip,
          index,
          priceCents,
          name: displayName,
          originalName: rate.name,
          province: this.domNodes.province?.value || '',
        });
      }

      clearRates() {
        window.clearTimeout(this.debounceTimer);

        if (this.context === 'product') {
          this.clearProductResults();
          return;
        }

        this.domNodes.message.innerHTML = '';
        this.rates = [];

        this.clearSelectedShipping(true);
      }

      clearProductResults() {
        if (this.domNodes.results) {
          this.domNodes.results.innerHTML = '';
          this.domNodes.results.hidden = true;
        }

        if (this.domNodes.total) {
          this.domNodes.total.hidden = true;
        }

        if (this.domNodes.totalPrice) {
          this.domNodes.totalPrice.textContent = '';
        }

        this.rates = [];
        this.selectedRate = null;
        this.selectedRateIndex = 0;
      }

      clearSelectedShipping(clearStorage = true) {
        this.selectedRate = null;
        this.selectedRateIndex = 0;

        this.classList.remove('has-rates');

        if (this.domNodes.price) {
          this.domNodes.price.textContent = '--';
        }

        if (this.domNodes.clearButton) {
          this.domNodes.clearButton.setAttribute('hidden', 'hidden');
        }

        this.updateTotal(null);

        if (clearStorage) {
          setStoredShipping(null);
        }
      }

      getSubtotalCents() {
        const subtotalWrapper = document.querySelector('.f-cart-drawer__block-subtotal[data-cart-subtotal-cents]');

        if (subtotalWrapper) {
          const subtotalCents = Number.parseInt(subtotalWrapper.dataset.cartSubtotalCents || '0', 10);

          if (Number.isFinite(subtotalCents) && subtotalCents > 0) {
            return subtotalCents;
          }
        }

        const priceElement = document.querySelector('[data-cart-price]');

        if (priceElement) {
          return parseMoneyToCents(priceElement.textContent);
        }

        return 0;
      }

      getOrCreateTotalRow() {
        let totalRow = document.querySelector('[data-cart-shipping-total-row]');

        if (totalRow) return totalRow;

        const subtotalRow = document.querySelector('.f-cart-drawer__block-subtotal .f-cart-drawer__totals');

        if (!subtotalRow) return null;

        totalRow = document.createElement('div');
        totalRow.className = 'f-cart-drawer__totals flex justify-between items-center';
        totalRow.setAttribute('data-cart-shipping-total-row', '');
        totalRow.classList.add('hidden');
        totalRow.setAttribute('hidden', 'hidden');
        totalRow.innerHTML = `
          <h3 class="f-cart-drawer__subtotal h6">Total:</h3>
          <p class="f-cart-drawer__subtotal-value" data-cart-shipping-total></p>
        `;

        subtotalRow.insertAdjacentElement('afterend', totalRow);

        return totalRow;
      }

      updateTotal(priceCents = undefined) {
        if (this.context !== 'cart') return;

        const totalRow = this.getOrCreateTotalRow();

        if (!totalRow) return;

        const totalValue = totalRow.querySelector('[data-cart-shipping-total]');

        if (!totalValue) return;

        let currentPriceCents = priceCents;

        if (typeof currentPriceCents === 'undefined' && this.selectedRate) {
          currentPriceCents = priceToCents(this.selectedRate);
        }

        if (currentPriceCents === null || typeof currentPriceCents === 'undefined') {
          totalRow.classList.add('hidden');
          totalRow.setAttribute('hidden', 'hidden');
          totalValue.textContent = '';
          return;
        }

        const subtotalCents = this.getSubtotalCents();

        totalValue.textContent = formatPrice(subtotalCents + currentPriceCents);
        totalRow.classList.remove('hidden');
        totalRow.removeAttribute('hidden');
      }

      updateProductTotal(shippingCents = 0) {
        if (this.context !== 'product') return;
        if (!this.domNodes.total || !this.domNodes.totalPrice) return;

        const subtotal = this.getCurrentProductSubtotalCents();
        const total = subtotal + Number(shippingCents || 0);

        this.domNodes.totalPrice.textContent = formatPrice(total);
        this.domNodes.total.hidden = false;
      }

      getProductForm() {
        const sectionEl = this.closest('[id^="shopify-section-"]');

        if (!sectionEl) {
          return document.querySelector('form[action*="/cart/add"]');
        }

        return sectionEl.querySelector('form[action*="/cart/add"]');
      }

      getCurrentVariantId() {
        const form = this.getProductForm();

        if (!form) return null;

        const variantField = form.querySelector('input[name="id"], select[name="id"]');

        if (!variantField || !variantField.value) return null;

        return Number(variantField.value);
      }

      getCurrentVariantPriceCents() {
        const variantId = this.getCurrentVariantId();

        if (!variantId || !Array.isArray(this.variants)) return 0;

        const variant = this.variants.find((item) => Number(item.id) === Number(variantId));

        return Number(variant?.price || 0);
      }

      getCurrentQuantity() {
        const form = this.getProductForm();

        if (!form) return 1;

        const qtyField = form.querySelector('input[name="quantity"]');
        const quantity = Number.parseInt(qtyField?.value || '1', 10);

        if (!Number.isFinite(quantity) || quantity < 1) return 1;

        return quantity;
      }

      getCurrentProductSubtotalCents() {
        return this.getCurrentVariantPriceCents() * this.getCurrentQuantity();
      }

      renderMessage(message) {
        this.domNodes.message.innerHTML = `<p class="f-cart-shipping__message-text">${message}</p>`;
        this.domNodes.message.hidden = false;
      }

      getErrorMessage(result) {
        return getErrorMessageFromResult(result);
      }

      setLoading(isLoading) {
        this.classList.toggle('is-loading', isLoading);

        if (this.domNodes.zipCode) {
          this.domNodes.zipCode.toggleAttribute('readonly', isLoading);
        }

        if (this.domNodes.calculateButton) {
          this.domNodes.calculateButton.disabled = isLoading;
          this.domNodes.calculateButton.textContent = isLoading
            ? this.loadingLabel || 'CALCULANDO...'
            : this.defaultButtonLabel || 'CALCULAR';
        }
      }
    }
  );
}
